import React, { useMemo, useState } from 'react';
import { Box, Button, ButtonBase, Chip, CircularProgress, Dialog, DialogContent, IconButton, Paper, Stack, TextField, Tooltip, Typography } from '@mui/material';
import Grid from '@mui/material/Grid2';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { CartesianGrid, Legend, LineChart, Line, ResponsiveContainer, ReferenceLine, Tooltip as ChartTooltip, XAxis, YAxis } from 'recharts';
import VitalGraphView from './VitalGraphView';
import { RangeChips, clickableTileSx, sectionCardSx, sectionTitleSx, trendHintSx as sharedTrendHintSx } from './dashboardStyles';

// Descriptions match MindVault's own wording (locales en.json: scan.metricGraph.description
// and scan.wellnessInfo) so therapist and client read the same explanation.
const VITAL_HELP = {
  'Heart Rate': 'Heart rate measures how many times the heart beats per minute, reflecting cardiovascular health and stress levels.',
  'Blood Pressure': 'Blood pressure measures the force of blood against artery walls. Camera estimate; use it as a trend, not a diagnosis.',
  'Oxygen Saturation': 'Oxygen saturation measures oxygen levels in the blood, reflecting respiratory efficiency and overall health.',
  'Respiratory Rate': 'Respiration rate is the number of breaths per minute, which can reflect stress, anxiety and physical condition.',
  'Stress Index': 'Stress index assesses the current stress level from physiological markers like heart rate variability and breathing patterns.',
  HRV: "HRV is the variation in time between heartbeats. Higher HRV generally indicates better resilience; compare with the client's own trend, not with other people.",
};
// Tile label -> VitalGraphView graph title.
const GRAPH_TITLE = {
  'Heart Rate': 'Heart Rate', 'Blood Pressure': 'Blood Pressure', 'Oxygen Saturation': 'Oxygen Saturation',
  'Respiratory Rate': 'Respiration Rate', 'Stress Index': 'Stress Index', HRV: 'Heart Rate Variability',
};
const INDICATORS = [
  { key: 'activity', label: 'Activity', help: "The overall function of the autonomic nervous system, reflecting the body's energy use and alertness." },
  { key: 'sleep', label: 'Sleep', help: 'The parasympathetic nerve activity, indicating how well the body recovers and relaxes.' },
  { key: 'equilibrium', label: 'Equilibrium', help: 'The balance between the sympathetic and parasympathetic systems, representing the balance between active and rest states.' },
  { key: 'metabolism', label: 'Metabolism', help: 'The sympathetic nerve activity, which reflects the autonomic control of metabolism.' },
  { key: 'health', label: 'Resilience', help: 'The dynamic complexity of the heartbeat, reflecting how adaptively the body can respond to physical, emotional or metabolic challenges.' },
  { key: 'relaxation', label: 'Relaxation', help: "The body's moment-to-moment adaptive capacity, representing current psychological stress tolerance." },
];
const RANGES = [{ key: 'last', label: 'Last scan' }, { key: 'week', label: 'This week' }, { key: 'month', label: 'This month' }, { key: 'custom', label: 'Custom' }];

const tileSx = { p: 1.5, border: '1px solid #E3E9EE', borderRadius: 1.5, bgcolor: '#FFFFFF', textAlign: 'left', display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: 0.5, width: '100%', height: '100%' };
const trendHintSx = sharedTrendHintSx;
const INDICATOR_COLORS = { activity: '#3B82F6', sleep: '#29C0BB', equilibrium: '#8B5CF6', metabolism: '#F59E0B', health: '#EF4444', relaxation: '#10B981' };

const InfoTip = ({ title, label }) => (
  <Tooltip title={title} arrow enterTouchDelay={0}>
    <IconButton size="small" aria-label={label} onClick={(event) => event.stopPropagation()} sx={{ p: 0.25, color: '#5B6675' }}>
      <InfoOutlinedIcon sx={{ fontSize: 16 }} />
    </IconButton>
  </Tooltip>
);

const StatusChip = ({ label, colors }) => (
  <Chip label={label} size="small" sx={{ height: 20, fontSize: 11, fontWeight: 600, bgcolor: colors.bg, color: colors.text }} />
);

// Presentation for the live client dashboard (the onboarding tour keeps WellnessOverviewView).
export default function WellnessSummaryView({ loading, latest, scans, range, setRange, customFrom, customTo, setCustomFrom, setCustomTo, setAppliedFrom, setAppliedTo,
  vitalCards, vitalGraphData, vitalDateRange, vitalStatusLabel, vitalStatusColor, scoreLabel, badgeColor, clampScore, formatDate }) {
  const [openGraph, setOpenGraph] = useState(null);
  const [openIndicators, setOpenIndicators] = useState(null);
  // Oldest first; -1 (indicator not computed) is left out of the line.
  const indicatorSeries = useMemo(() => {
    const ordered = [...(scans || [])].reverse();
    const perDay = ordered.reduce((counts, s) => ({ ...counts, [formatDate(s.created_at)]: (counts[formatDate(s.created_at)] || 0) + 1 }), {});
    // Several scans on one day would repeat the same label, so add the time.
    const label = (s) => perDay[formatDate(s.created_at)] > 1
      ? `${formatDate(s.created_at)} ${new Date(s.created_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`
      : formatDate(s.created_at);
    return ordered.map((s) => ({
    date: label(s),
    ...Object.fromEntries(INDICATORS.map((i) => [i.key, scoreLabel(s[i.key]) === 'Not Available' ? null : clampScore(s[i.key])])),
  }));
  }, [scans, formatDate, scoreLabel, clampScore]);

  const hrv = useMemo(() => {
    const values = (scans || []).map((s) => Number(s.hr_variability)).filter((n) => Number.isFinite(n) && n > 0);
    if (!values.length) return null;
    const latestValue = values[0];
    const average = values.reduce((sum, n) => sum + n, 0) / values.length;
    let comparison = null;
    if (values.length >= 3) {
      if (latestValue < average * 0.9) comparison = { label: 'Below usual', color: '#B45309' };
      else if (latestValue > average * 1.1) comparison = { label: 'Above usual', color: '#29C0BB' };
      else comparison = { label: 'Around usual', color: '#5B6675' };
    }
    return { latestValue, average, count: values.length, comparison, series: [...values].reverse().map((value) => ({ value })) };
  }, [scans]);

  const tiles = (vitalCards || []).filter((card) => card.label !== 'HRV');
  const pickRange = (key) => {
    setRange(key);
    if (key === 'custom') { setCustomFrom(''); setCustomTo(''); setAppliedFrom(''); setAppliedTo(''); }
  };

  return (
    <Paper elevation={0} sx={sectionCardSx}>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={1} sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h6" sx={sectionTitleSx}>Wellness overview</Typography>
          {latest?.created_at && <Typography variant="caption" sx={{ color: '#5B6675' }}>Latest scan {formatDate(latest.created_at)}</Typography>}
        </Box>
        <RangeChips options={RANGES} value={range} onChange={pickRange} label="Scan period" />
      </Stack>

      {range === 'custom' && (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} sx={{ mb: 2 }}>
          <TextField type="date" size="small" label="Start date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} InputLabelProps={{ shrink: true }} />
          <TextField type="date" size="small" label="End date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} InputLabelProps={{ shrink: true }} />
          <Button size="small" variant="contained" onClick={() => { setAppliedFrom(customFrom); setAppliedTo(customTo); }}
            sx={{ textTransform: 'none', fontWeight: 600, bgcolor: '#29C0BB' }}>Apply</Button>
        </Stack>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={28} /></Box>
      ) : !latest ? (
        <Typography variant="body2" sx={{ color: '#5B6675' }}>No scans in this period.</Typography>
      ) : (
        <Stack spacing={2}>
          <Grid container spacing={1.5}>
            <Grid size={{ xs: 12, md: 5 }}>
              <ButtonBase component="div" onClick={() => setOpenGraph('HRV')} aria-label="Show HRV over time"
                sx={{ ...tileSx, p: 2, gap: 1, bgcolor: '#F0FDFA', borderColor: '#B6DAD7', '&:hover': { borderColor: '#29C0BB' } }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#29C0BB' }}>HRV (heart rate variability)</Typography>
                    <InfoTip title={VITAL_HELP.HRV} label="What HRV means" />
                  </Stack>
                  {hrv?.comparison && <Typography sx={{ fontSize: 12, fontWeight: 600, color: hrv.comparison.color }}>{hrv.comparison.label}</Typography>}
                </Stack>
                <Stack direction="row" alignItems="baseline" spacing={0.75}>
                  <Typography sx={{ fontSize: 32, fontWeight: 800, color: '#14234B', lineHeight: 1.1 }}>{hrv ? Math.round(hrv.latestValue) : '--'}</Typography>
                  <Typography sx={{ fontSize: 13, color: '#5B6675' }}>
                    ms{hrv && hrv.count > 1 ? ` · average ${Math.round(hrv.average)} ms over ${hrv.count} scans` : ''}
                  </Typography>
                </Stack>
                {hrv && hrv.count > 1 && (
                  <Box sx={{ height: 48 }} aria-hidden="true">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={hrv.series} margin={{ top: 4, right: 4, bottom: 4, left: 4 }}>
                        <YAxis hide domain={['dataMin - 5', 'dataMax + 5']} />
                        <ReferenceLine y={hrv.average} stroke="#9AA4B1" strokeDasharray="4 4" />
                        <Line type="monotone" dataKey="value" stroke="#29C0BB" strokeWidth={2.5} dot={false} isAnimationActive={false} />
                      </LineChart>
                    </ResponsiveContainer>
                  </Box>
                )}
                <Typography sx={{ ...trendHintSx, alignSelf: 'flex-end' }}>View trend ›</Typography>
              </ButtonBase>
            </Grid>
            <Grid size={{ xs: 12, md: 7 }}>
              <Grid container spacing={1.5}>
                {tiles.map((card) => {
                  const colors = vitalStatusColor(card.status);
                  return (
                    <Grid key={card.label} size={{ xs: 6, sm: 4, md: 6, lg: 4 }}>
                      <ButtonBase component="div" onClick={() => setOpenGraph(card.label)} aria-label={`Show ${card.label.toLowerCase()} over time`}
                        sx={{ ...tileSx, '&:hover': { borderColor: '#29C0BB' } }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={0.5}>
                          <Stack direction="row" alignItems="center" spacing={0.25} sx={{ minWidth: 0 }}>
                            <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>{card.label}</Typography>
                            <InfoTip title={VITAL_HELP[card.label] || ''} label={`What ${card.label.toLowerCase()} means`} />
                          </Stack>
                          <StatusChip label={vitalStatusLabel(card.status)} colors={colors} />
                        </Stack>
                        <Stack direction="row" justifyContent="space-between" alignItems="baseline" spacing={0.5}>
                          <Typography sx={{ fontSize: 18, fontWeight: 700, color: '#14234B' }}>
                            {card.value}{card.unit && card.value !== '--' ? <Box component="span" sx={{ fontSize: 12, fontWeight: 500, color: '#5B6675', ml: 0.5 }}>{card.unit}</Box> : null}
                          </Typography>
                          <Typography sx={trendHintSx}>Trend ›</Typography>
                        </Stack>
                      </ButtonBase>
                    </Grid>
                  );
                })}
              </Grid>
            </Grid>
          </Grid>

          <Box sx={{ borderTop: '1px solid #E3E9EE', pt: 2 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 1.5 }}>
              <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#14234B' }}>Wellness indicators</Typography>
              <Stack direction="row" spacing={1.5} alignItems="baseline">
                <Typography variant="caption" sx={{ color: '#5B6675' }}>Scored 0–5 from the same scan</Typography>
                <ButtonBase onClick={() => setOpenIndicators('all')} sx={{ ...trendHintSx, p: 0.5, borderRadius: 1 }}>View all trends ›</ButtonBase>
              </Stack>
            </Stack>
            <Grid container spacing={1.5}>
              {INDICATORS.map((indicator) => {
                const raw = latest[indicator.key];
                const label = scoreLabel(raw);
                const available = label !== 'Not Available';
                const score = clampScore(raw);
                return (
                  <Grid key={indicator.key} size={{ xs: 6, sm: 4, lg: 2 }}>
                    <Box role="button" tabIndex={0} aria-label={`Show ${indicator.label.toLowerCase()} over time`} onClick={() => setOpenIndicators(indicator.key)}
                      onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); setOpenIndicators(indicator.key); } }}
                      sx={{ ...tileSx, ...clickableTileSx, gap: 0.75 }}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={0.5}>
                        <Stack direction="row" alignItems="center" spacing={0.25}>
                          <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>{indicator.label}</Typography>
                          <InfoTip title={indicator.help} label={`What ${indicator.label.toLowerCase()} means`} />
                        </Stack>
                        {available && <Chip label={label} size="small" sx={{ height: 20, fontSize: 11, fontWeight: 600, bgcolor: `${badgeColor(label)}1F`, color: badgeColor(label) }} />}
                      </Stack>
                      <Typography sx={{ fontSize: 18, fontWeight: 700, color: '#14234B' }}>
                        {available ? score.toFixed(1) : '--'}<Box component="span" sx={{ fontSize: 12, fontWeight: 500, color: '#5B6675', ml: 0.5 }}>/ 5</Box>
                      </Typography>
                      <Stack direction="row" spacing={0.375} aria-hidden="true">
                        {[1, 2, 3, 4, 5].map((step) => (
                          <Box key={step} sx={{ flex: 1, height: 6, borderRadius: 3, bgcolor: available && step <= Math.round(score) ? badgeColor(label) : '#E3E9EE' }} />
                        ))}
                      </Stack>
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        </Stack>
      )}

      <Dialog open={Boolean(openIndicators)} onClose={() => setOpenIndicators(null)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ px: 3, pt: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {openIndicators === 'all' ? 'Wellness indicators over time' : `${INDICATORS.find((i) => i.key === openIndicators)?.label || ''} over time`}
          </Typography>
          <IconButton onClick={() => setOpenIndicators(null)} aria-label="Close"><CloseIcon /></IconButton>
        </Stack>
        <DialogContent>
          {indicatorSeries.length < 2 && <Typography variant="body2" sx={{ color: '#5B6675', mb: 1 }}>Only one scan in this period. Choose This month or Custom to see a trend.</Typography>}
          <Box sx={{ height: 300 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={indicatorSeries} margin={{ top: 10, right: 20, bottom: 0, left: -10 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                <YAxis domain={[0, 5]} ticks={[0, 1, 2, 3, 4, 5]} tick={{ fontSize: 12 }} />
                <ChartTooltip />
                {openIndicators === 'all' && <Legend />}
                {INDICATORS.filter((i) => openIndicators === 'all' || i.key === openIndicators).map((i) => (
                  <Line key={i.key} type="monotone" dataKey={i.key} name={i.label} stroke={INDICATOR_COLORS[i.key]} strokeWidth={2.5} dot={{ r: 3 }} connectNulls isAnimationActive={false} />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </Box>
          {openIndicators && openIndicators !== 'all' && <Typography variant="body2" sx={{ mt: 1.5, color: '#5B6675' }}>{INDICATORS.find((i) => i.key === openIndicators)?.help}</Typography>}
          <Typography variant="caption" sx={{ display: 'block', mt: 1, color: '#5B6675' }}>Above 3.3 is Excellent, above 1.7 is Good, below that is Poor (MindVault's scale).</Typography>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(openGraph)} onClose={() => setOpenGraph(null)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ px: 3, pt: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>{openGraph === 'HRV' ? 'HRV (heart rate variability)' : openGraph} over time</Typography>
          <IconButton onClick={() => setOpenGraph(null)} aria-label="Close"><CloseIcon /></IconButton>
        </Stack>
        <DialogContent>
          {openGraph && <VitalGraphView data={vitalGraphData} dateRange={vitalDateRange} only={GRAPH_TITLE[openGraph]} />}
          {openGraph && VITAL_HELP[openGraph] && <Typography variant="body2" sx={{ mt: 1.5, color: '#5B6675' }}>{VITAL_HELP[openGraph]}</Typography>}
        </DialogContent>
      </Dialog>
    </Paper>
  );
}
